/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import DataTable, { SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@stoneage/App/Components/Notify";
import deleteRegisteredUser from "@stoneage/App/Handlers/CourseManagement/DeleteRegisteredUser";
import getRegisteredUsers from "@stoneage/App/Handlers/CourseManagement/GetRegisteredUsers";
import updateRegisteredUser from "@stoneage/App/Handlers/CourseManagement/UpdateRegisteredUser";
import { CourseManagementModel } from "@stoneage/App/Services/CourseManagement/CourseManagementService";
import CourseManagementEditStudentButton from "@stoneage/App/Widgets/CourseManagement/CourseManagementEditStudentButton";
import Pagination from "@stoneage/MobiusOverrides/Pagination/Pagination";
import { parseBoolean, parseDate, xScreen } from "@stoneage/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface CourseManagementCourseTableStyles {
    tableCell?: DataTableCellProps;
    emailCell?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    reclaimSeatButton?: LinkPresentationProps;
    reclaimModal?: TwoButtonModalStyles;
}
export const courseManagementCourseTableStyles: CourseManagementCourseTableStyles = {
    tableCell: {
        css: css`
            font-size: 14px;
            line-height: 22px;
            font-weight: 400;
        `,
    },
    emailCell: {
        css: css`
            font-weight: bold;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    reclaimSeatButton: {
        color: "common.black",
        typographyProps: {
            size: "12px",
            lineHeight: "18px",
            color: "common.black",
        },
        icon: {
            iconProps: {
                src: "IconClear",
                size: 8,
                color: "common.ui6",
                css: css`
                    margin-right: 4px;
                `,
            },
        },
    },
    reclaimModal: {
        modal: {
            size: 600,
        },
        buttonsGridItem: {
            width: 12,
            css: css`
                &&&& {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
            `,
        },
    },
};

const styles = courseManagementCourseTableStyles;

export interface CourseManagementCourseTableProps {
    courseId: string;
    registeredUsers: CourseManagementModel[];
    updateRegisteredUser: HandleThunkActionCreator<typeof updateRegisteredUser>;
    deleteRegisteredUser: HandleThunkActionCreator<typeof deleteRegisteredUser>;
    getRegisteredUsers: HandleThunkActionCreator<typeof getRegisteredUsers>;
    updateCourseState: (courseId: string) => void;
    paginationData: any;
}

const CourseManagementCourseTable = ({
    courseId,
    registeredUsers,
    updateRegisteredUser,
    deleteRegisteredUser,
    getRegisteredUsers,
    paginationData,
    updateCourseState,
}: CourseManagementCourseTableProps) => {
    const toasterContext = useContext(ToasterContext);
    const [registeredUsersArray, setRegisteredUsersArray] = useState<CourseManagementModel[]>([]);
    const [isLoading, setIsloading] = useState(false);
    const { totalItemCount, numberOfPages } = paginationData;
    const [reclaimModalOpen, setReclaimModalOpen] = useState(false);
    const [reclaimParams, setReclaimParams] = useState({
        registrationId: "",
        courseId: "",
    });
    const [sortData, setSortData] = useState({
        page: 1,
        pageSize: 8,
        sortBy: "",
    });

    const { hasReclaimPermission } = useAppSelector(state => {
        const session = getSession(state);
        return {
            hasReclaimPermission: parseBoolean(session.properties?.canReclaimSeats),
        };
    });

    useEffect(() => {
        setRegisteredUsersArray(registeredUsers);
    }, [registeredUsers]);

    const getRegisteredUsersQuery = (newSortData: { page: number; pageSize: number; sortBy: string }) => {
        setIsloading(true);
        getRegisteredUsers({
            ...newSortData,
            courseId,
            onError: error => {
                setIsloading(false);
            },
            onSuccess(resultProps) {
                if (resultProps?.successful) {
                    resultProps.result.courseRegistrations &&
                        setRegisteredUsersArray(resultProps.result.courseRegistrations);
                } else if (resultProps?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.errorMessage);
                }
                setIsloading(false);
            },
        });
        setSortData(newSortData);
    };

    const headerClick = (sortField: string) => {
        const direction = sortData.sortBy === sortField ? " DESC" : "";
        const sortBy = sortField + direction;
        const newSortData = {
            ...sortData,
            sortBy,
        };
        getRegisteredUsersQuery(newSortData);
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (sortData.sortBy === sortField) {
            sorted = "ascending";
        } else if (sortData.sortBy === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    const changePage = (newPageIndex: number) => {
        const newSortData = {
            ...sortData,
            page: newPageIndex,
        };
        getRegisteredUsersQuery(newSortData);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        const newSortData = {
            ...sortData,
            pageSize: newPageSize,
        };
        getRegisteredUsersQuery(newSortData);
    };

    const reclaimUserHandler = () => {
        const { registrationId, courseId } = reclaimParams;
        deleteRegisteredUser({
            courseId,
            registrationId,
            onError: error => {
                if (error === undefined) {
                    toasterContext.addToast({
                        body: translate("Student has been successfully Reclaimed"),
                        messageType: "success",
                    });
                    const registeredUsers = [...registeredUsersArray];
                    const index = registeredUsers.findIndex(
                        user => user.id === registrationId && user.courseId === courseId,
                    );
                    if (index > -1) {
                        registeredUsers[index].reclaimed = true;
                        setRegisteredUsersArray(registeredUsers);
                        updateCourseState(courseId);
                    }
                }
                if (error) {
                    toasterContext.addToast({ body: error, messageType: "danger" });
                }
                setReclaimModalOpen(false);
            },
            onSuccess(resultProps) {
                if (resultProps?.successful) {
                    toasterContext.addToast({
                        body: translate("Student has been successfully Reclaimed"),
                        messageType: "success",
                    });
                } else if (resultProps?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.errorMessage);
                }
                setReclaimModalOpen(false);
            },
        });
    };

    const handleCancelCloseModal = () => {
        setReclaimModalOpen(false);
    };

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!registeredUsersArray.length) {
        return <Notify messageType="info" body={translate("No Registered Users Found")} />;
    }

    return (
        <>
            <StyledWrapper
                css={css`
                    width: 100%;
                    ${xScreen("gt", "sm")} {
                        overflow-x: auto;
                    }
                `}
            >
                <DataTable className="mobile-view">
                    <DataTableHead>
                        <DataTableHeader
                            tight
                            sorted={sorted("createdOn")}
                            onSortClick={() => headerClick("createdOn")}
                            data-test-selector="tableHeader_Reg_date"
                        >
                            {translate("Reg. Date")}
                        </DataTableHeader>
                        <DataTableHeader
                            tight
                            sorted={sorted("firstName")}
                            onSortClick={() => headerClick("firstName")}
                            data-test-selector="tableHeader_firstName"
                        >
                            {translate("First Name")}
                        </DataTableHeader>
                        <DataTableHeader
                            tight
                            sorted={sorted("lastName")}
                            onSortClick={() => headerClick("lastName")}
                            data-test-selector="tableHeader_lastName"
                        >
                            {translate("Last Name")}
                        </DataTableHeader>
                        <DataTableHeader tight data-test-selector="tableHeader_customerNumber">
                            {translate("Customer #")}
                        </DataTableHeader>
                        <DataTableHeader
                            tight
                            sorted={sorted("company")}
                            onSortClick={() => headerClick("company")}
                            data-test-selector="tableHeader_company"
                        >
                            {translate("Company")}
                        </DataTableHeader>
                        <DataTableHeader
                            tight
                            sorted={sorted("email")}
                            onSortClick={() => headerClick("email")}
                            data-test-selector="tableHeader_email"
                        >
                            {translate("Email")}
                        </DataTableHeader>
                        <DataTableHeader
                            tight
                            sorted={sorted("percentageCompleted")}
                            onSortClick={() => headerClick("percentageCompleted")}
                            data-test-selector="tableHeader_status"
                        >
                            {translate("% Completed")}
                        </DataTableHeader>
                        {hasReclaimPermission && <DataTableHeader tight></DataTableHeader>}
                    </DataTableHead>
                    <DataTableBody>
                        {registeredUsersArray.map(
                            ({
                                registrationDate,
                                firstName,
                                lastName,
                                customerNumber,
                                company,
                                email,
                                id,
                                reclaimed,
                                percentageCompleted,
                            }) => (
                                <DataTableRow key={id}>
                                    <DataTableCell data-th={translate("Reg. Date")} {...styles.tableCell}>
                                        {parseDate(registrationDate)}
                                    </DataTableCell>
                                    <DataTableCell data-th={translate("First Name")} {...styles.tableCell}>
                                        {firstName}
                                    </DataTableCell>
                                    <DataTableCell data-th={translate("Last Name")} {...styles.tableCell}>
                                        {lastName}
                                    </DataTableCell>
                                    <DataTableCell data-th={translate("Customer #")} {...styles.tableCell}>
                                        {customerNumber}
                                    </DataTableCell>
                                    <DataTableCell data-th={translate("Company")} {...styles.tableCell}>
                                        {company}
                                    </DataTableCell>
                                    <DataTableCell {...styles.emailCell} data-th={translate("Email")}>
                                        {email}
                                    </DataTableCell>
                                    <DataTableCell data-th={translate("% Completed")} {...styles.tableCell}>
                                        {percentageCompleted}%
                                    </DataTableCell>
                                    {hasReclaimPermission && !reclaimed && (
                                        <DataTableCell {...styles.tableCell}>
                                            <Link
                                                {...styles.reclaimSeatButton}
                                                onClick={() => {
                                                    setReclaimParams({
                                                        registrationId: id,
                                                        courseId,
                                                    });
                                                    setReclaimModalOpen(true);
                                                }}
                                            >
                                                {translate("Reclaim Seat")}
                                            </Link>
                                        </DataTableCell>
                                    )}
                                    {hasReclaimPermission && reclaimed && (
                                        <DataTableCell {...styles.tableCell}>{translate("Reclaimed")}</DataTableCell>
                                    )}
                                </DataTableRow>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            <TwoButtonModal
                modalIsOpen={reclaimModalOpen}
                dataTestSelector="ReclaimStudentModal"
                headlineText={translate("Reclaim Student")}
                messageText={siteMessage("Custom_claimStudentConfirmationMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Reclaim")}
                submitTestSelector="ReclaimStudentButton"
                onCancel={handleCancelCloseModal}
                onSubmit={reclaimUserHandler}
                extendedStyles={styles.reclaimModal}
            />
            {numberOfPages > 1 && (
                <Pagination
                    resultsCount={totalItemCount}
                    currentPage={sortData.page}
                    resultsPerPage={sortData.pageSize}
                    resultsPerPageOptions={totalItemCount}
                    onChangePage={changePage}
                    onChangeResultsPerPage={changeResultsPerPage}
                    isBottom={true}
                    pageHasMultiplePaginations={true}
                />
            )}
        </>
    );
};

export default CourseManagementCourseTable;
